/* eslint-disable max-len */
import {
	AfterViewInit,
	Component,
	ElementRef,
	ViewChild,
	OnDestroy,
	OnInit
} from '@angular/core';
import { FormControl } from '@angular/forms';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { AuthService } from '@auth0/auth0-angular';
import { EVENTS } from '@constants/analytics';
import { ROUTES } from '@constants/routes';
import { USAA } from '@constants/usaa';
import { BrandConfig } from '@models/BrandConfig';
import { BreedService } from '@pages/pet-information/breed.service';
import { AnalyticsService } from '@services/analytics.service';
import { ExperimentationService } from '@services/experimentation.service';
import { LoggingService } from '@services/logging.service';
import { OrchestratorService } from '@services/orchestrator/orchestrator.service';
import { SeoService } from '@services/seo.service';
import { UtmService } from '@services/utm.service';

import { Observable, Subject, firstValueFrom } from 'rxjs';
import {
	distinctUntilChanged,
	filter,
	first,
	map,
	mergeMap,
	take,
	takeUntil,
} from 'rxjs/operators';

import { environment } from '../environments/environment';

import { QuoteSyncService } from './services/quote-sync.service';
import { SignalingConfigService } from './services/signaling-config.service';
import { SignalingDebugService } from './services/signaling-debug.service';

import { UserTokenService } from './services/user-token.service';
import { ApplicationQuery } from './store/application/application.query';
import { ApplicationService } from './store/application/application.service';
import { BrandConfigQuery } from './store/brand/brand.query';
import { BrandConfigService } from './store/brand/brand.service';
import { BreedsService } from './store/breeds/breeds.service';
import { QuoteSessionQuery } from './store/session/session.query';
import { QuoteSessionService } from './store/session/session.service';
import { formatPhoneNumber } from './utils/phone-number-formatter';
import { WhitelabelService } from './whitelabel.service';

@Component({
	standalone: false,
	selector: 'qe-root',
	templateUrl: './app.component.html',
	styleUrls: ['./app.component.scss'],
})
export class AppComponent implements OnInit, AfterViewInit, OnDestroy {
	@ViewChild('epiLargeLoader')
		epiLargeLoader?: ElementRef<HTMLEpiLargeLoaderElement>;
	@ViewChild('loadingDialog')
		loadingDialog?: ElementRef<HTMLDialogElement>;

	public readonly isProdEnvironment: boolean = environment.production;
	public readonly loadingText$: Observable<string>;
	public readonly loading$!: Observable<boolean>;
	public showDevRoutes: FormControl = new FormControl(false);
	public whitelabelLogoUrl: string = ``;
	public readonly ROUTES = ROUTES;
	public readonly canAddMorePets$: Observable<boolean>;
	public contactPhoneNumber: Observable<string | undefined>;
	public brandConfig$: Observable<BrandConfig>;
	public testName: string = 'none';
	public testVariation: string = 'not assigned';
	public usaaSsoInactive: boolean = false;
	public showProgressBar: boolean = false;
	private styleLocation: string = '';
	private utmCampaignParam?: string | null = '';
	private readonly ngUnsubscribe$: Subject<boolean> = new Subject();
	private readonly trackedScrollDepths = new Set<number>();
	private scrollListener?: () => void;
	private remoteQuoteRefreshScheduled = false;
	private pendingRemoteQuoteRefresh = false;
	private pendingRemoteQuoteId: string | null = null;

	constructor(
		private readonly hostEl: ElementRef<HTMLElement>,
		private readonly loggingService: LoggingService,
		private readonly activatedRoute: ActivatedRoute,
		private readonly router: Router,
		private readonly appActions: ApplicationService,
		private readonly appQuery: ApplicationQuery,
		private readonly session: QuoteSessionService,
		private readonly sessionGetters: QuoteSessionQuery,
		private readonly whitelabel: WhitelabelService,
		private readonly gtmService: AnalyticsService,
		private readonly utmService: UtmService,
		private readonly brandConfigActions: BrandConfigService,
		private readonly brandGetters: BrandConfigQuery,
		private readonly breed: BreedService,
		private readonly breedsState: BreedsService,
		private readonly auth: AuthService,
		private readonly userTokenService: UserTokenService,
		private readonly orchestratorService: OrchestratorService,
		private readonly seoService: SeoService,
		private readonly experimentationService: ExperimentationService,
		private readonly quoteSyncService: QuoteSyncService,
		private readonly signalingConfigService: SignalingConfigService,
		private readonly signalingDebugService: SignalingDebugService
	) {
		// Initialize observables after dependency injection
		this.canAddMorePets$ = this.sessionGetters.canAddMorePets$;
		this.contactPhoneNumber = this.brandGetters.phone$();

		const navigationEntries: PerformanceNavigationTiming[] = (<unknown>(
			performance.getEntriesByType('navigation')
		)) as PerformanceNavigationTiming[];
		const isLegacy: boolean = !navigationEntries.length;
		firstValueFrom(
			this.sessionGetters.sessionId$().pipe(map((id) => !!id))
		).then((hasSession) => {
			const pageRefreshed: boolean = isLegacy
				? performance.navigation.type === performance.navigation.TYPE_RELOAD
				: navigationEntries[0].type === 'reload';

			if (!pageRefreshed && hasSession) {
				this.session.reset();
			}
		});

		this.loadingText$ = this.appQuery.largeLoaderText$();
		this.brandConfig$ = this.brandGetters.brandConfig$();

		// Force debug service initialization and emit a startup readiness snapshot.
		this.logSignalingReadiness();

		this.breed
			.getAllBreeds()
			.pipe(take(1))
			.subscribe((breeds) => {
				this.breedsState.update(breeds);
			});

		this.router.events
			.pipe(
				filter((event) => event instanceof NavigationEnd),
				map(() => this.activatedRoute),
				map((route) => {
					while (route.firstChild) {
						route = route.firstChild;
					}

					return route;
				}),
				mergeMap((route) => route.data),
				takeUntil(this.ngUnsubscribe$)
			)
			.subscribe((data) => {
				this.appActions.update({ workflowStep: data?.step });
			});

		// Scroll to top on all route changes
		this.router.events
			.pipe(
				filter((event) => event instanceof NavigationEnd)
			)
			.subscribe((event) => {
				const navigationEnd = event as NavigationEnd;
				this.seoService.updateCanonicalUrl(navigationEnd.urlAfterRedirects);
				// Check if we're on the congratulations page
				this.showProgressBar = !navigationEnd.urlAfterRedirects.includes(ROUTES.CONGRATULATIONS);

				setTimeout(() => {
					this.hostEl.nativeElement.
						scrollTo({
							top: 0,
							left: 0,
							behavior: `smooth`
						});
					this.setupScrollTracking(window.location?.href ?? (event as NavigationEnd).urlAfterRedirects);
				}, 0);
			});
		// Update canonical link tag on every route change
		this.router.events
			.pipe(
				filter((event) => event instanceof NavigationEnd),
				takeUntil(this.ngUnsubscribe$)
			)
			.subscribe((event) => {
				this.seoService.updateCanonicalUrl((event as NavigationEnd).urlAfterRedirects);
			});

		this.sessionGetters
			.brand$()
			.pipe(
				filter((brand) => !!brand),
				map((brand) => ({ raw: brand, formatted: brand.toLowerCase() })),
				distinctUntilChanged(),
				takeUntil(this.ngUnsubscribe$)

			)

			.subscribe(async (brand) => {
				const utmParams = this.utmService.getUtmCookie();

				this.appActions.setLoading(true);
				this.whitelabel
					.getPartnerData(brand.formatted, utmParams)
					.pipe(first())
					.subscribe((config) => {
						this.brandConfigActions.update(config);

						const formattedPhoneNumber = formatPhoneNumber(
							config.partnerPhoneNumber
						);

						this.styleLocation =
							config.whiteLabelConfig.quote.partnerAssets.styleSheetLocation;
						this.whitelabel.setWhitelabelStylesheet(
							brand.formatted,
							this.styleLocation
						);

						this.gtmService.push({
							event: `Quote Engine brand set`,
							// TODO: Remove this brand value after Google Analytics 4 changes are live
							brand: brand.raw,
							brandName: brand.formatted,
							brandPhoneNumber: config.partnerPhoneNumber,
							brandFormattedPhoneNumber: formattedPhoneNumber,
						});
					});

				if (brand.formatted === 'usaa') {
					const sessionId = await firstValueFrom(
						this.sessionGetters.sessionId$()
					);

					const urlParams = new URLSearchParams(window.location.search);
					this.utmCampaignParam = urlParams.get('utm_campaign');
					// if the state was already set in the guard via traits
					// OR we have a campaign utm saved in the cookie
					// OR utm_campaign is in the url
					this.usaaSsoInactive = this.appQuery.isUsaaSsoInactiveFlow$()
						|| utmParams.campaign === USAA.SSO_INACTIVE
						|| this.utmCampaignParam === USAA.SSO_INACTIVE;

					// set sso in app state for use later on contact info form
					this.appActions.setUsaaSsoInactive(this.usaaSsoInactive);

					// stop SSO path if it is a retrieve or non-sso path
					if (this.isRetrieveUrl || this.usaaSsoInactive || !!sessionId) {
						this.appActions.setLoading(false);

						return;
					}
					this.auth.isLoading$
						.pipe(takeUntil(this.ngUnsubscribe$))
						.subscribe((loading) => {
							if (!loading) {
								this.auth.isAuthenticated$
									.pipe(takeUntil(this.ngUnsubscribe$))
									.subscribe({
										next: (isAthenticated) => {
											if (isAthenticated) {
												this.auth
													.getAccessTokenSilently({
														authorizationParams: {
															redirectUri: environment?.USAAAuth?.callbackURL,
														}
													})
													.pipe(first())
													.subscribe({
														next: (_token) => {
															this.auth.user$
																.pipe(takeUntil(this.ngUnsubscribe$))
																.subscribe({
																	next: (user) => {
																		const memberId =
																			this.userTokenService.getMemberNumberFromToken(
																				user
																			);
																		if (memberId === null) return;
																		this.appActions.setLoading(
																			true,
																			undefined,
																			undefined,
																			this.constructor.name
																		);
																		this.session.updateContact({
																			organizationMemberIdentifier: memberId,
																		});
																		this.orchestratorService
																			.getShouldRedirect(memberId)
																			.pipe(first())
																			.subscribe({
																				next: (result) => {
																					if (!result) {
																						this.userTokenService.setUserData(
																							user
																						);
																						this.appActions.setLoading(
																							false,
																							undefined,
																							undefined,
																							this.constructor.name
																						);

																						this.router.navigate([ROUTES.PET_INFORMATION], {
																							queryParams: { brand: 'usaa', orgCode: '80056341' }
																						});

																						return;
																					}
																					location.href =
																						environment?.USAAAuth?.USAARedirect;
																				},
																				error: (err) => {
																					this.appActions.setLoading(
																						false,
																						undefined,
																						undefined,
																						this.constructor.name
																					);
																					this.loggingService.error(err);
																				},
																			});
																	},
																	error: (err) =>
																		this.loggingService.error(err),
																});
														},

														error: (err) => this.loggingService.error(err),
													});

												return;
											}
											this.auth
												.loginWithRedirect({
													authorizationParams: {
														redirect_uri: environment.USAAAuth.callbackURL,
														connection: environment.USAAAuth.connection,
													}
												})
												.pipe(first())
												.subscribe({
													next: () => {
														this.auth
															.getAccessTokenSilently({
																authorizationParams: {
																	redirectUri: environment?.USAAAuth?.callbackURL,
																}
															})
															.pipe(first())
															.subscribe({
																next: (token) => {
																	this.userTokenService.setUserData(token);
																},
																error: (err) => {
																	this.loggingService.error(err);
																},
															});
													},
													error: (err) =>
														this.loggingService.error(
															'loginWithRedirect - Error:',
															err
														),
												});
										},
										error: (err) => this.loggingService.error(err),
									});
							}
						});
				} else {
					this.appActions.setUsaaSsoInactive(false);
				}
				this.appActions.setLoading(false);
			});
	}

	private logSignalingReadiness(): void {
		void this.signalingDebugService;
		const readiness = this.signalingConfigService.getReadiness();
		this.loggingService.info({
			message: 'Signaling readiness smoke check',
			readiness,
		});
	}

	public get showMaxPetsWarning$(): Observable<boolean> {
		return this.sessionGetters.canAddMorePets$.pipe(
			map(canAddMore => !canAddMore && !this.router.url.includes(ROUTES.CONGRATULATIONS))
		);
	}

	public get isRetrieveUrl(): boolean {
		return (
			!!location.search.length &&
			/email/i.test(location.search) &&
			/zip|zip_code|zipCode/.test(location.search)
		);
	}

	public get formattedPhoneNumber(): string | undefined {
		let formattedPhoneNumber: string | undefined;
		this.contactPhoneNumber.pipe(take(1)).subscribe((phoneNumber) => {
			formattedPhoneNumber = formatPhoneNumber(phoneNumber);
		});

		return formattedPhoneNumber;
	}

	async ngOnInit(): Promise<void> {
		this.sessionGetters
			.sessionId$()
			.pipe(
				filter((sessionId) => !!sessionId),
				distinctUntilChanged(),
				takeUntil(this.ngUnsubscribe$),
			)
			.subscribe((sessionId) => {
				this.quoteSyncService.initializeForQuote(sessionId);
			});

		this.quoteSyncService.syncReceived$
			.pipe(
				filter((quoteId) => !!quoteId),
				filter((quoteId) => quoteId === this.sessionGetters.getQuoteId()),
				takeUntil(this.ngUnsubscribe$),
			)
			.subscribe((quoteId: string) => {
				this.queueRemoteQuoteRefresh(quoteId);
			});

		try {
			await this.experimentationService.initializeClient();
		} catch (error) {
			this.loggingService.error(`[AppComponent] Failed to initialize experimentation client: ${error}`);
		}
	}

	ngAfterViewInit(): void {
		this.appQuery.loading$().pipe(takeUntil(this.ngUnsubscribe$)).subscribe(loading => {
			// Control custom element visibility (legacy behavior)
			if (loading) {
				this.epiLargeLoader?.nativeElement.show?.();
			} else {
				this.epiLargeLoader?.nativeElement.hide?.();
			}

			const el = this.loadingDialog?.nativeElement as HTMLDialogElement & { showModal?: () => void; open?: boolean; close?: () => void };
			if (!el) return;
			if (loading) {
				if (!el.open) {
					try { el.showModal?.(); } catch { /* swallow DOMException if already shown */ }
				}
			} else if (el.open) {
				el.close?.();
			}
		});
	}

	ngOnDestroy(): void {
		// Remove scroll listener
		if (this.scrollListener) {
			window.removeEventListener('scroll', this.scrollListener);
		}

		this.ngUnsubscribe$.next(true);
		this.ngUnsubscribe$.unsubscribe();
	}

	private queueRemoteQuoteRefresh(quoteId: string): void {
		this.pendingRemoteQuoteId = quoteId;

		if (this.remoteQuoteRefreshScheduled) {
			this.pendingRemoteQuoteRefresh = true;
			this.loggingService.info(`[QuoteSync] Remote update queued while sync is in progress for ${quoteId}.`);

			return;
		}

		const pendingQuoteId = this.pendingRemoteQuoteId;
		this.pendingRemoteQuoteId = null;

		if (!pendingQuoteId) {
			return;
		}

		this.refreshQuoteFromRemoteUpdate(pendingQuoteId);
	}

	private refreshQuoteFromRemoteUpdate(quoteId: string): void {
		this.remoteQuoteRefreshScheduled = true;
		this.quoteSyncService.setSyncRefreshInProgress(true);
		this.loggingService.info(`[QuoteSync] Remote update received for ${quoteId}. Refetching quote.`);

		firstValueFrom(this.orchestratorService.saveQuote('quote-sync-refresh'))
			.then((session) => {
				this.session.update(session, false);
				this.loggingService.info(`[QuoteSync] Quote synced for ${quoteId}.`);
			})
			.catch((error) => {
				this.loggingService.error('[QuoteSync] Failed to sync remote quote update.', error as string);
			})
			.finally(() => {
				this.remoteQuoteRefreshScheduled = false;
				if (this.pendingRemoteQuoteRefresh) {
					this.pendingRemoteQuoteRefresh = false;
					const pendingQuoteId = this.pendingRemoteQuoteId || quoteId;
					this.pendingRemoteQuoteId = null;
					this.loggingService.info('[QuoteSync] Processing queued remote update.');
					this.refreshQuoteFromRemoteUpdate(pendingQuoteId);

					return;
				}

				this.quoteSyncService.setSyncRefreshInProgress(false);
			});
	}

	public setupScrollTracking(pagePath: string): void {
		const PERCENTAGE_MULTIPLIER = 100;
		const SCROLL_INCREMENT = 10;
		const MAX_SCROLL_PERCENTAGE = 100;

		// Reset tracked depths for new page
		this.trackedScrollDepths.clear();

		// Remove existing listener if it exists
		if (this.scrollListener) {
			window.removeEventListener('scroll', this.scrollListener);
		}

		// Create new listener
		this.scrollListener = () => {
			const scrollTop = window.scrollY;
			const docHeight = document.documentElement.scrollHeight - window.innerHeight;

			// Prevent division by zero
			if (docHeight <= 0) return;

			const scrollPercent = Math.floor((scrollTop / docHeight) * PERCENTAGE_MULTIPLIER);

			// Track every 10% increment that hasn't been tracked yet
			for (let milestone = SCROLL_INCREMENT; milestone <= scrollPercent && milestone <= MAX_SCROLL_PERCENTAGE; milestone += SCROLL_INCREMENT) {
				if (!this.trackedScrollDepths.has(milestone)) {
					this.trackedScrollDepths.add(milestone);

					this.gtmService.push({
						event: EVENTS.SCROLL,
						scrollDepth: milestone,
						pagePath
					});
				}
			}
		};

		// Add the new listener
		window.addEventListener('scroll', this.scrollListener);
	}
}
